import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Container, Typography, Grid } from '@mui/material'
import SEOMetaTags from '@/components/SEOMetaTags'
import EmailSubscribeDialog from '@/components/EmailSubscribeDialog'
import DonateDialog from '@/components/DonateDialog'
import { donationContent } from '@/data'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'
import {
  generateArticleSchema,
  generateFAQPageSchema,
  generateBreadcrumbSchema,
} from '@/utils/schema'
import Breadcrumbs from '@/components/research/Breadcrumbs'
import KeyFindingCard from '@/components/research/KeyFindingCard'
import ExpertCallout from '@/components/research/ExpertCallout'
import FAQAccordion from '@/components/research/FAQAccordion'
import SourceCitations from '@/components/research/SourceCitations'
import CTABlock from '@/components/research/CTABlock'
import RelatedPages from '@/components/research/RelatedPages'
import { overnightsAndParentingPlans } from '@/data/research/overnights-parenting-plans'
import type { ResearchPageData } from '@/data/research/types'

const PRODUCTION_URL = 'https://ncesp.org'

/** Registry of research pages keyed by slug */
const researchPageRegistry: Record<string, ResearchPageData> = {
  'overnights-and-parenting-plans': overnightsAndParentingPlans,
}

const ResearchPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false)
  const [isDonateDialogOpen, setIsDonateDialogOpen] = useState(false)

  const pageData = slug ? researchPageRegistry[slug] : undefined

  if (!pageData) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4">Page not found</Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          The research page you are looking for does not exist.
        </Typography>
      </Container>
    )
  }

  const seoConfig = getSEOConfig(`/research/${pageData.slug}`)
  const canonicalUrl = getCanonicalUrl(`/research/${pageData.slug}`)

  const articleSchema = generateArticleSchema(pageData)
  const faqSchema = generateFAQPageSchema(pageData.faqItems)
  const breadcrumbSchemaData = generateBreadcrumbSchema([
    { name: 'Home', url: `${PRODUCTION_URL}/` },
    { name: 'Research', url: `${PRODUCTION_URL}/research` },
    { name: pageData.breadcrumbLabel, url: canonicalUrl },
  ])

  const formatSourceAttribution = (sourceId: number): string => {
    const source = pageData.sourceCitations.find(s => s.id === sourceId)
    if (!source) return ''
    return `Source: ${source.authors.split(',')[0]}${source.authors.includes(',') ? ' et al.' : ''} (${source.year})`
  }

  return (
    <Box component="main" role="main">
      <SEOMetaTags
        title={seoConfig?.title ?? pageData.metaTitle}
        description={seoConfig?.description ?? pageData.metaDescription}
        canonical={canonicalUrl}
        ogImage={seoConfig?.ogImage ?? '/images/og/default.jpg'}
        keywords={seoConfig?.keywords ?? pageData.keywords}
        schemas={[articleSchema, faqSchema, breadcrumbSchemaData]}
      />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Breadcrumbs
          items={[
            { label: 'Home', path: '/' },
            { label: 'Research', path: '/research' },
          ]}
          currentPage={pageData.breadcrumbLabel}
        />

        <Typography
          variant="h1"
          sx={{ mb: 3, fontSize: { xs: '1.75rem', md: '3rem' } }}
        >
          {pageData.heading}
        </Typography>

        {pageData.introduction.map((paragraph, i) => (
          <Typography key={i} variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            {paragraph}
          </Typography>
        ))}

        {pageData.sections.map((section, sectionIndex) => (
          <Box key={sectionIndex} component="section" sx={{ mt: 5 }}>
            <Typography
              variant="h2"
              sx={{ mb: 2, fontSize: { xs: '1.35rem', md: '2.5rem' } }}
            >
              {section.heading}
            </Typography>

            {section.body.map((paragraph, i) => (
              <Typography
                key={i}
                variant="body1"
                sx={{ mb: 2, lineHeight: 1.8 }}
              >
                {paragraph}
              </Typography>
            ))}

            {section.keyFindings && section.keyFindings.length > 0 && (
              <Grid container spacing={3} sx={{ mt: 2 }}>
                {section.keyFindings.map((finding, i) => (
                  <Grid item key={i} xs={12} md={6}>
                    <KeyFindingCard
                      statement={finding.statement}
                      context={finding.context}
                      sourceAttribution={formatSourceAttribution(
                        finding.sourceId
                      )}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ))}

        {pageData.expertCallout && (
          <ExpertCallout
            quote={pageData.expertCallout.quote}
            expertName={pageData.expertCallout.expertName}
            credentials={pageData.expertCallout.credentials}
            linkUrl={pageData.expertCallout.linkUrl}
            linkText={pageData.expertCallout.linkText}
          />
        )}

        <FAQAccordion items={pageData.faqItems} />

        <CTABlock
          heading={pageData.ctaBlock.heading}
          body={pageData.ctaBlock.body}
          actions={pageData.ctaBlock.actions}
          onSubscribe={() => setIsEmailDialogOpen(true)}
          onDonate={() => setIsDonateDialogOpen(true)}
        />

        <RelatedPages pages={pageData.relatedPages} />

        <SourceCitations
          citations={pageData.sourceCitations}
          disclaimer="The research summarized on this page was conducted by independent researchers and does not necessarily represent the official position of the National Council for Equal Shared Parenting."
        />
      </Container>

      <EmailSubscribeDialog
        open={isEmailDialogOpen}
        onClose={() => setIsEmailDialogOpen(false)}
      />

      <DonateDialog
        open={isDonateDialogOpen}
        onClose={() => setIsDonateDialogOpen(false)}
        content={donationContent}
      />
    </Box>
  )
}

export default ResearchPage
